<script setup lang="ts">
// retrieve folder notes for current folder/file
// render plaintext when editing
// render markdown when previewing

import type { MDCParserResult } from "@nuxtjs/mdc"

import { parseMarkdown } from "@nuxtjs/mdc/runtime"

import Checkbox from "~/components/Markdown/MarkdownCheckbox.vue" // only one kind of input

const { navIgnoreMainViewGestures } = useGlobals()
const { state: folderNotesState, startEditingFolderNotes, stopEditingFolderNotes } = useFolderNotes()
const { state: viewModeState } = useViewMode()

const editable = useTemplateRef("editable")

async function started() {
  startEditingFolderNotes()
  await nextTick()
  editable.value!.$el.focus()
}
function returned() {
  stopEditingFolderNotes()
}
const visible = computed(() => viewModeState.value.current.general.notes)
const content = ref<MDCParserResult | undefined>(undefined)
watchImmediate(() => folderNotesState.value.content, async notes => content.value = await parseMarkdown(notes))
</script>

<template>
  <div v-if="visible" class="folder-notes">
    <interface-content-editable
      v-show="folderNotesState.isEditing"
      ref="editable"
      v-model="folderNotesState.content"
      tag="div"
      contenteditable="plaintext-only"
      class="folder-notes-content"
      :class="[navIgnoreMainViewGestures]"
      newlines
      spellcheck="false"
      @blur="returned()"
    />
    <div
      v-if="!folderNotesState.isEditing && folderNotesState.content.trim()"
      class="folder-notes-not-editing"
      @click="started()"
    >
      <m-d-c-renderer
        v-if="content"
        :body="content.body"
        :data="content.data"
        :components="{
          input: Checkbox,
        }"
        class="folder-notes-markdown"
      />
    </div>
    <div
      v-else-if="!folderNotesState.isEditing"
      class="placeholder"
      @click="started()"
    >
      Add Notes..
    </div>
  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.folder-notes
  @apply rounded-[#{$menu-br-input}] -m-1 p-1 cursor-text

.folder-notes-not-editing
  @apply rounded-[#{$menu-br-input}] -m-1 p-1
  &:hover
    @apply bg-[#{$page-foldernotes-bg-note}] cursor-text

.folder-notes-content
  @apply whitespace-normal overflow-auto text-base break-words
  @apply multiline bg-transparent
  @apply rounded-md px-2.5 py-1.5 bg-[#{$menu-bg-input-text-box}]
  &:focus
    @apply outline outline-2 outline-$global-blue-primary

.placeholder
  @apply text-size-$global-fs-base fw-$global-fw-base tracking-$global-ls-base text-[#{$page-foldernotes-t-note-hover}]
</style>

<style lang="sass">
@import '~/assets/styles/generated/variables.sass'

// This styling is not scoped since vue can't apply styling to a dynamic markdown
// component. So instead we must scope everything to this class
.folder-notes-markdown
  @apply space-y-2.25
  hr
    @apply border-$global-steel-secondary border-t-0.75
  code
    @apply text-$global-t-secondary bg-$global-steel-accent
  h1
    @apply text-size-$page-properties-2xl-fs fw-semibold tracking-$page-properties-2xl-ls leading-$page-properties-2xl-lh
    &>code
      @apply px-1.25 py-0.25 rounded-sm fw-semibold
  * + h1
    @apply mt-4
  h2
    @apply text-size-$page-properties-xl-fs fw-semibold tracking-$page-properties-xl-ls leading-$page-properties-xl-lh
    &>code
      @apply px-0.75 py-0.25 rounded-sm fw-semibold
  * + h2
    @apply mt-3
  h3
    @apply text-size-$page-properties-lg-fs fw-semibold tracking-$page-properties-lg-ls leading-$page-properties-lg-lh
    &>code
      @apply px-0.75 py-0.25 rounded-sm fw-semibold
  * + h3
    @apply mt-2
  h4
    @apply text-size-$page-properties-md-fs fw-bold tracking-$page-properties-md-ls leading-$page-properties-md-lh
    &>code
      @apply px-0.5 py-0.25 rounded-sm fw-semibold
  * + h4
    @apply mt-1.5
  h5
    @apply text-size-$page-properties-sm-fs fw-bold tracking-$page-properties-sm-ls leading-$page-properties-sm-lh
    &>code
      @apply px-0.5 py-0.25 rounded-sm fw-semibold
  * + h5
    @apply mt-1
  h6
    @apply text-size-$page-properties-xs-fs fw-bold tracking-$page-properties-xs-ls leading-$page-properties-xs-lh
    &>code
      @apply px-0.5 py-0.25 rounded-sm fw-semibold
  * + h6
    @apply mt-1
  p
    @apply text-size-$global-fs-base fw-$global-fw-base tracking-$global-ls-base
  strong
    @apply font-bold
  p>code
    @apply px-0.5 py-0.25 rounded-sm
  blockquote
    @apply border-l-1.5 border-$global-steel-secondary pl-3.5 italic text-$global-t-secondary

  ul:not(.contains-task-list)
    @apply pl-4
    li
      @apply list-disc list-outside 

  ul.contains-task-list
    @apply space-y-1.5 my-1
    li.task-list-item
      @apply flex items-start gap-1.5
      .checkbox
        @apply mt-0.25
        input
          @apply cursor-text

  ol
    @apply pl-4 mt-1.5 space-y-1.5
    li
      @apply list-decimal list-outside

  a
    @apply text-$global-violet-primary

  pre
    @apply border border-$menu-b-input-default rounded-md p-2

</style>
